import type { FC } from 'react';
import React, { useState, useEffect, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import AkButton from '@atlaskit/button/button';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';

import { ControlPanelContextProvider } from './ControlPanelContext.tsx';
import { ButtonContainer } from './presenterModeStyles.tsx';
import { LazyNavigation, LazySettingsMenu } from './LazyButtons.tsx';
// eslint-disable-next-line import/no-cycle
import { usePresenterModeContext } from './PresenterModeContext.tsx';

export const HeaderControlPanel = 'presenter-mode-control-panel';

const headerContainerStyles = css({
	alignItems: 'center',
	backgroundColor: token('elevation.surface.overlay'),
	position: 'fixed',
	top: token('space.300'),
	right: token('space.300'),
	display: 'flex',
	zIndex: '800',
	padding: token('space.150'),
	boxShadow: token('elevation.shadow.overlay'),
	borderRadius: token('radius.small'),
});

const verticalDividerStyles = css({
	height: '24px',
	width: '0px',
	borderLeft: `${token('border.width')} solid ${token('color.border')}`,
});

const rightControlPanelStyles = css({
	marginLeft: 'auto',
	display: 'flex',
	justifyContent: 'flex-end',
	alignItems: 'center',
});

type HeaderPresentationModeProps = {
	onSwitchCursorClick: (newValue: boolean) => void;
	contentRendered: boolean;
	spaceKey: string;
	contentId: string;
};

const i18n = defineMessages({
	exit: {
		id: 'presentation-mode.header.exit',
		description:
			'The text is shown on the Exit button in the presentation mode header. When the user clicks this button, they leave the presenter mode and return to the normal page view.',
		defaultMessage: 'Exit',
	},
});

export const HeaderPresenterMode: FC<HeaderPresentationModeProps> = ({
	onSwitchCursorClick,
	contentRendered,
	spaceKey,
	contentId,
}) => {
	const { exitPresenterMode, isBrowserFullscreen, setIsBrowserFullscreen } =
		usePresenterModeContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const [showLazyButtons, setShowLazyButtons] = useState(false);
	const spaceId = useSpaceId(spaceKey);

	useEffect(() => {
		const timeout = setTimeout(() => {
			setShowLazyButtons(true);
		}, 0);
		return () => {
			clearTimeout(timeout);
		};
	}, []);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'viewPresentScreen',
				attributes: {
					objectId: contentId,
					containerId: spaceId,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, contentId, spaceId]);

	const exit = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'present',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'close',
				objectId: contentId,
				containerId: spaceId,
			},
		}).fire();
		exitPresenterMode();
	}, [createAnalyticsEvent, exitPresenterMode, contentId, spaceId]);

	return (
		<TransparentErrorBoundary attribution={Attribution.COMMENTS}>
			<ControlPanelContextProvider>
				<div data-testid="presentation-mode-header">
					<div css={headerContainerStyles} id={HeaderControlPanel}>
						<div css={rightControlPanelStyles}>
							<LazyNavigation
								contentRendered={contentRendered}
								contentId={contentId}
								spaceKey={spaceKey}
							/>
							<div css={verticalDividerStyles} />
							<LazySettingsMenu
								onSwitchCursorClick={onSwitchCursorClick}
								contentId={contentId}
								spaceKey={spaceKey}
							/>
							{contentRendered && showLazyButtons && (
								<ButtonContainer id="presenter-mode-exit-button">
									<AkButton
										testId="presentation-mode-button-close"
										onClick={isBrowserFullscreen ? () => setIsBrowserFullscreen(false) : exit}
									>
										{intl.formatMessage(i18n.exit)}
									</AkButton>
								</ButtonContainer>
							)}
						</div>
					</div>
				</div>
			</ControlPanelContextProvider>
		</TransparentErrorBoundary>
	);
};
